import { environment } from '../environments/environment';

export class Config {
  private static mergedConfig: any = null;
  private static configLoaded = false;
  private static loadStartTime: number = 0;
  private static loadEndTime: number = 0;

  /**
   * Load configuration from backend.json file and merge with environment.ts
   * backend.json values take priority over environment.ts values
   */
  static async loadConfig(): Promise<void> {
    this.loadStartTime = performance.now();

    try {
      const response = await fetch('/backend.json');
      const backendConfig = await response.json();

      // Merge environment with backend.json, giving priority to backend.json values
      this.mergedConfig = this.deepMerge(environment, backendConfig);
      this.configLoaded = true;
      this.loadEndTime = performance.now();
      console.log('[Config] Configuration loaded successfully from backend.json');
    } catch (error) {
      this.loadEndTime = performance.now();
      // Use only environment.ts if backend.json is not available
      this.mergedConfig = { ...environment };
      this.configLoaded = true;
      console.warn('[Config] backend.json not available, using environment.ts defaults');
    }
  }

  /**
   * Get the entire merged environment configuration
   * Returns environment.ts merged with backend.json (backend.json has priority)
   */
  static get environment() {
    if (!this.configLoaded) {
      return environment;
    }
    return this.mergedConfig;
  }

  /**
   * Check if configuration is loaded
   */
  static isConfigLoaded(): boolean {
    return this.configLoaded;
  }

  /**
   * Get configuration load time in milliseconds
   */
  static getLoadTime(): number {
    if (this.loadEndTime === 0 || this.loadStartTime === 0) {
      return 0;
    }
    return Number((this.loadEndTime - this.loadStartTime).toFixed(2));
  }

  /**
   * Get detailed performance metrics
   */
  static getPerformanceMetrics(): {
    loadTime: number;
    startTime: string;
    endTime: string;
    isLoaded: boolean;
  } {
    return {
      loadTime: this.getLoadTime(),
      startTime: this.loadStartTime > 0 ? new Date(Date.now() - (performance.now() - this.loadStartTime)).toISOString() : '',
      endTime: this.loadEndTime > 0 ? new Date(Date.now() - (performance.now() - this.loadEndTime)).toISOString() : '',
      isLoaded: this.configLoaded
    };
  }

  /**
   * Deep merge two objects, giving priority to the second object's values
   */
  private static deepMerge(target: any, source: any): any {
    const result = { ...target };

    for (const key in source) {
      if (source[key] !== null && typeof source[key] === 'object' && !Array.isArray(source[key])) {
        if (target[key] && typeof target[key] === 'object' && !Array.isArray(target[key])) {
          result[key] = this.deepMerge(target[key], source[key]);
        } else {
          result[key] = { ...source[key] };
        }
      } else {
        result[key] = source[key];
      }
    }

    return result;
  }
}
